import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { FaFileExcel, FaFilePdf, FaFilter, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import api from "../services/api";

const DeviceReports = () => {
  const { user } = useAuth() || {};
  
  const userRole = user?.role || user?.type || "Super Admin";
  
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [dateRange, setDateRange] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [imeiSearch, setImeiSearch] = useState('');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Check if user is Super Admin
  const isSuperAdmin = userRole === 'Super Admin';

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (e) {
      return dateString;
    }
  };

const loadDevices = async () => {
  setLoading(true);
  try {
    
    const response = await api.get("/device-history");  
    const formatted = response.data.map(device => ({
      id: device.id,
      companyName: device.company_name || "N/A",
      imeiNumber: device.imei || "N/A",
      vehicleNumber: device.vehicle_number || "N/A",
      installationDate: device.installation_date,
      removeDate: device.removed_date,
      warrantyDate: device.installation_warranty_end,
      status: device.removed_date ? "Removed" : "Installed",
      installerName: device.new_installer_name || device.installer_name,
      changeType: device.change_type,
      deviceType: device.device_type || 'GPS'
    }));
    
    setDevices(formatted);
    
    const uniqueCompanies = [...new Set(formatted.map(d => d.companyName).filter(Boolean))].sort();
    setCompanies(uniqueCompanies);
    
  } catch (error) {
    console.error("❌ Error loading devices:", error);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    loadDevices();
  }, []);

  // Filter devices based on criteria
  useEffect(() => {
    let filtered = [...devices];
    
    if (companyFilter) {
      filtered = filtered.filter(d => 
        String(d.companyName).toLowerCase().trim() === String(companyFilter).toLowerCase().trim()
      );
    }

    if (imeiSearch.trim()) {
      filtered = filtered.filter(d =>
        String(d.imeiNumber || '')
          .toLowerCase()
          .includes(imeiSearch.toLowerCase())
      );
    }
    
    const now = new Date();
    let startDateObj = null;
    let endDateObj = new Date();
    
    if (dateRange === '7days') {
      startDateObj = new Date();
      startDateObj.setDate(now.getDate() - 7);
    } else if (dateRange === '15days') {
      startDateObj = new Date();
      startDateObj.setDate(now.getDate() - 15);
    } else if (dateRange === '1month') {
      startDateObj = new Date();
      startDateObj.setMonth(now.getMonth() - 1);
    } else if (dateRange === '3months') {
      startDateObj = new Date();
      startDateObj.setMonth(now.getMonth() - 3);
    } else if (dateRange === 'calendar' && startDate && endDate) {
      startDateObj = new Date(startDate);
      startDateObj.setHours(0, 0, 0, 0);
      endDateObj = new Date(endDate);
      endDateObj.setHours(23, 59, 59, 999);
    }
    
    if (startDateObj) {
      filtered = filtered.filter(d => {
        const deviceDate = d.installationDate ? new Date(d.installationDate) : new Date(0);
        if (dateRange === 'calendar') {
          return deviceDate >= startDateObj && deviceDate <= endDateObj;
        }
        return deviceDate >= startDateObj;
      });
    }
    
    setFilteredDevices(filtered);
    setCurrentPage(1);
  }, [devices, dateRange, startDate, endDate, companyFilter, imeiSearch]);

  // Pagination calculations
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentPageData = filteredDevices.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredDevices.length / rowsPerPage);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Export to Excel Handler
  const exportToExcel = () => {
    if (filteredDevices.length === 0) {
      alert("No data available to export!");
      return;
    }

    const exportData = filteredDevices.map(d => ({
      'Company Name': d.companyName,
      'IMEI Number': d.imeiNumber,
      'Vehicle Number': d.vehicleNumber,
      'Installer': d.installerName || 'N/A',
      'Installation Date': formatDate(d.installationDate),
      'Remove Date': formatDate(d.removeDate),
      'Warranty Date': formatDate(d.warrantyDate),
      'Status': d.status || 'Active',
      'Device Type': d.deviceType || 'GPS',
      'Change Type': d.changeType || 'Installation'
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    ws['!cols'] = [
      { wch: 25 },
      { wch: 20 },
      { wch: 20 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 }
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Device Reports');
    
    const fileName = `device_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Export to PDF Handler
  const exportToPDF = () => {
    if (filteredDevices.length === 0) {
      alert("No data available to export!");
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4');
    
    doc.setFontSize(18);
    doc.text('Device Reports', 14, 18);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 26);
    doc.text(`Date Range Filter: ${getDateRangeText()}`, 14, 32);
    if (companyFilter) {
      doc.text(`Company Filter: ${companyFilter}`, 14, 38);
    }
    const startY = companyFilter ? 44 : 38;
    doc.text(`Total Devices: ${filteredDevices.length}`, 14, startY);
    
    const tableData = filteredDevices.map(d => [
      d.companyName,
      d.imeiNumber,
      d.vehicleNumber,
      d.installerName || 'N/A',
      formatDate(d.installationDate),
      formatDate(d.removeDate),
      formatDate(d.warrantyDate),
      d.status || 'Active',
      d.deviceType || 'GPS',
      d.changeType || 'Installation'
    ]);
    
    doc.autoTable({
      startY: startY + 6,
      head: [['Company', 'IMEI Number', 'Vehicle Number', 'Installer', 'Installation Date', 'Remove Date', 'Warranty Date', 'Status', 'Device Type', 'Change Type']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontSize: 8, fontStyle: 'bold' },
      styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 28 },
        2: { cellWidth: 22 },
        3: { cellWidth: 20 },
        4: { cellWidth: 20 },
        5: { cellWidth: 20 },
        6: { cellWidth: 20 },
        7: { cellWidth: 16 },
        8: { cellWidth: 16 },
        9: { cellWidth: 22 }
      }
    });
    
    doc.save(`device_report_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const getDateRangeText = () => {
    if (dateRange === '7days') return 'Last 7 days';
    if (dateRange === '15days') return 'Last 15 days';
    if (dateRange === '1month') return 'Last 1 month';
    if (dateRange === '3months') return 'Last 3 months';
    if (dateRange === 'calendar') return `${startDate} to ${endDate}`;
    return 'All time';
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive': return 'bg-red-100 text-red-800 border-red-200';
      case 'maintenance': return 'bg-amber-100 text-amber-800 border-amber-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getChangeTypeBadgeClass = (changeType) => {
    switch (changeType) {
      case 'VEHICLE_NUMBER_CHANGE': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'INSTALLER_CHANGE': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'VEHICLE_AND_INSTALLER_CHANGE': return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'INSTALLATION': return 'bg-green-50 text-green-700 border-green-200';
      case 'REMOVED': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-[60vh] bg-gray-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md">
          <div className="text-red-500 text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-600">This page is only accessible to Super Admin users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen font-sans">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Device Reports Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">View and export device installation reports with filtering options</p>
      </div>

      {/* Control Filters Panel */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <FaFilter className="text-indigo-500" /> Filter Device Records
        </h2>
        
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Quick Select Range</label>
            <select
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value);
                if (e.target.value !== 'calendar') {
                  setStartDate('');
                  setEndDate('');
                }
              }}
              className="border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 min-w-[160px]"
            >
              <option value="all">All Time</option>
              <option value="7days">Last 7 Days</option>
              <option value="15days">Last 15 Days</option>
              <option value="1month">Last 1 Month</option>
              <option value="3months">Last 3 Months</option>
              <option value="calendar">Custom Date Range</option>
            </select>
          </div>

          {dateRange === 'calendar' && (
            <>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Filter by Company</label>
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 min-w-[180px]"
            >
              <option value="">All Companies</option>
              {companies.map((company) => (
                <option key={company} value={company}>{company}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Search IMEI</label>
            <input
              type="text"
              value={imeiSearch}
              onChange={(e) => setImeiSearch(e.target.value)}
              placeholder="Enter IMEI Number"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 min-w-[220px]"
            />
          </div>

          {(companyFilter || imeiSearch) && (
            <div>
              <button
                onClick={() => {
                  setCompanyFilter('');
                  setImeiSearch('');
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition"
              >
                Clear Filters
              </button>
            </div>
          )}

          <div className="flex gap-2 ml-auto">
            <button
              onClick={exportToExcel}
              disabled={filteredDevices.length === 0}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold shadow-sm transition ${
                filteredDevices.length === 0 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
            >
              <FaFileExcel /> Export Excel
            </button>
            <button
              onClick={exportToPDF}
              disabled={filteredDevices.length === 0}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold shadow-sm transition ${
                filteredDevices.length === 0 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              <FaFilePdf /> Export PDF
            </button>
          </div>
        </div>

        {(companyFilter || imeiSearch) && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-500">Active Filters:</span>
            {companyFilter && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-full border border-purple-200">
                Company: {companyFilter}
                <button onClick={() => setCompanyFilter('')} className="hover:text-purple-900">✕</button>
              </span>
            )}
            {imeiSearch && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                IMEI: {imeiSearch}
                <button onClick={() => setImeiSearch('')} className="hover:text-blue-900">✕</button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Live Data Preview Grid Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50/50">
          <h2 className="text-base font-semibold text-gray-800">Device Details</h2>
          <span className="text-xs font-semibold bg-gray-200 text-gray-700 px-2.5 py-1 rounded-full">
            Records Found: {filteredDevices.length}
          </span>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <>
            {/* Table container with fixed height and scroll */}
            <div className="overflow-x-auto" style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-100 text-xs font-bold text-gray-500 uppercase border-b border-gray-200 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3">Company Name</th>
                    <th className="px-4 py-3">IMEI Number</th>
                    <th className="px-4 py-3">Vehicle Number</th>
                    <th className="px-4 py-3">Installer</th>
                    <th className="px-4 py-3">Installation Date</th>
                    <th className="px-4 py-3">Remove Date</th>
                    <th className="px-4 py-3">Warranty Date</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Change Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-sm text-gray-700">
                  {currentPageData.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-12 text-center text-gray-400 font-medium italic">
                        No device records found for the selected filters.
                      </td>
                    </tr>
                  ) : (
                    currentPageData.map((device, index) => (
                      <tr key={device.id || index} className="hover:bg-gray-50/80 transition-colors">
                        <td className="px-4 py-3 font-semibold">{device.companyName}</td>
                        <td className="px-4 py-3 font-mono text-xs font-bold text-blue-600">{device.imeiNumber}</td>
                        <td className="px-4 py-3 font-mono text-sm font-semibold text-gray-700">
                          {device.vehicleNumber}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {device.installerName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-gray-500">{formatDate(device.installationDate)}</td>
                        <td className="px-4 py-3 text-xs font-mono text-red-500 whitespace-nowrap">
                          {device.removeDate ? formatDate(device.removeDate) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-green-600 font-semibold">
                          {device.warrantyDate ? formatDate(device.warrantyDate) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full border ${getStatusBadgeClass(device.status)}`}>
                            {device.status || 'Active'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <span className={`px-2 py-0.5 rounded-full border font-medium ${getChangeTypeBadgeClass(device.changeType)}`}>
                            {device.changeType || 'Installation'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {filteredDevices.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center bg-gray-50/50 gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-gray-600">Rows per page:</label>
                  <select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    className="border border-gray-300 bg-white rounded-md px-2 py-1 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                <div className="text-xs text-gray-600 font-medium">
                  {filteredDevices.length > 0 ? (
                    <>
                      <span className="font-bold text-gray-900">{indexOfFirstRow + 1}</span>
                      <span className="mx-1">–</span>
                      <span className="font-bold text-gray-900">
                        {Math.min(indexOfLastRow, filteredDevices.length)}
                      </span>
                      <span className="mx-1">of</span>
                      <span className="font-bold text-gray-900">{filteredDevices.length}</span>
                    </>
                  ) : (
                    <span>0 entries</span>
                  )}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-lg border text-xs font-semibold shadow-sm transition flex items-center gap-1 ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
                      }`}
                    >
                      <FaChevronLeft className="w-3 h-3" /> Prev
                    </button>
                    
                    <span className="text-xs font-bold text-gray-700 px-3 py-1 bg-gray-100 rounded-md border border-gray-200">
                      {currentPage} of {totalPages}
                    </span>

                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-lg border text-xs font-semibold shadow-sm transition flex items-center gap-1 ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
                      }`}
                    >
                      Next <FaChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DeviceReports;